/**
 * History panel: groups stored hail events by calendar day (newest first) and lets the user pick
 * one, routing the selection back to the app (which loads its detail + frames the map). Pure DOM.
 *
 * An event the viewer has not opened yet carries a "nový" badge, so a fresh detection is visible
 * at a glance (the in-app half of the 'alarm on new hail' ask; out-of-app notification is a
 * separate backlog item). "Seen" lives in localStorage — per browser, no backend state: opening
 * the detail (from the list OR the map) marks it seen and the badge goes away for good.
 */

import type { HailEventSummary } from './api.ts';

const SEEN_KEY = 'kroupy-seen-event-ids';
// Cap the stored ids so the value cannot grow forever; oldest ids fall off, which is harmless —
// events that old are no longer "new" to anyone.
const SEEN_CAP = 500;

function loadSeen(): Set<number> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]');
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is number => typeof v === 'number') : [],
    );
  } catch {
    return new Set(); // private mode / corrupted value — everything simply shows as new
  }
}

function saveSeen(seen: Set<number>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-SEEN_CAP)));
  } catch {
    // storage unavailable (private mode/quota) — the badge just reappears next visit
  }
}

interface DayGroup {
  day: string; // YYYY-MM-DD, used as the heading key
  label: string; // localized day label
  events: HailEventSummary[];
}

function groupByDay(events: HailEventSummary[]): DayGroup[] {
  const byDay = new Map<string, HailEventSummary[]>();
  for (const event of events) {
    const day = event.occurred_at.slice(0, 10);
    const bucket = byDay.get(day);
    if (bucket) bucket.push(event);
    else byDay.set(day, [event]);
  }
  return [...byDay.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([day, dayEvents]) => ({
      day,
      label: new Date(`${day}T00:00:00`).toLocaleDateString('cs-CZ', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
      events: dayEvents,
    }));
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

export class History {
  constructor(
    private readonly root: HTMLElement,
    private readonly onSelect: (id: number) => void,
  ) {}

  /** Mark an event as seen (list click, map click — any way its detail opens) and drop its badge. */
  markSeen(id: number): void {
    const seen = loadSeen();
    if (seen.has(id)) return;
    seen.add(id);
    saveSeen(seen);
    this.root.querySelector(`[data-badge-for="${id}"]`)?.remove();
  }

  render(events: HailEventSummary[]): void {
    if (events.length === 0) {
      this.root.innerHTML = `<p class="history-empty">Žádné uložené zásahy.</p>`;
      return;
    }
    const seen = loadSeen();
    const groups = groupByDay(events);
    this.root.innerHTML = groups
      .map(
        (group) => `
        <section class="history-day">
          <h4>${escapeHtml(group.label)}</h4>
          <ul>
            ${group.events
              .map((event) => {
                const size =
                  event.hail_size_cm == null
                    ? ' · velikost neznámá'
                    : ` · ${event.hail_size_cm.toFixed(1)} cm`;
                const time = new Date(event.occurred_at).toLocaleTimeString('cs-CZ', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                const badge = seen.has(event.id)
                  ? ''
                  : `<span class="badge-new" data-badge-for="${event.id}">nový</span>`;
                return `<li><button type="button" data-event-id="${event.id}">${escapeHtml(time)}${escapeHtml(size)}${badge}</button></li>`;
              })
              .join('')}
          </ul>
        </section>`,
      )
      .join('');

    this.root.querySelectorAll<HTMLButtonElement>('button[data-event-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = Number(button.dataset['eventId']);
        if (!Number.isNaN(id)) this.onSelect(id);
      });
    });
  }
}
